import {useMemo} from "react";
import {RootState, useAppSelector} from "../../../store";
import {IProfileWish, IWish, WishType} from "../../../store/wish/Helper";
import {computeSeededTicketsForWish} from "./TicketsCountLinearProgressHelper";

interface UseWishTicketAvailabilityParams {
    wish: IWish | IProfileWish
    accumulateTotals?: boolean
}

interface UseWishTicketAvailabilityResult {
    current: number
    total: number
    remaining: number
}

const useWishTicketAvailability = ({
                                       wish,
                                       accumulateTotals = false
                                   }: UseWishTicketAvailabilityParams): UseWishTicketAvailabilityResult => {
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);

    const bundledWishes = useMemo(() => wish.bundled_wishes ?? [], [wish.bundled_wishes]);
    const wishes = useMemo(() => {
        if (wish.type === WishType.BUNDLE) {
            return [wish];
        }

        return [wish, ...(wish.children ?? [])];
    }, [wish]);

    const getCurrentTickets = (currentWish: IWish | IProfileWish, nowMs: number) => (
        computeSeededTicketsForWish({
            id: currentWish.id,
            actual: currentWish.tickets_count,
            max: currentWish.max_tickets,
            startISO: currentWish.start_time,
            endISO: currentWish.end_time,
            nowMs,
        })
    );

    const total = useMemo(
        () =>
            accumulateTotals
                ? wishes.reduce((accumulator, currentWish) => accumulator + currentWish.max_tickets, 0)
                : wish.max_tickets,
        [accumulateTotals, wish.max_tickets, wishes]
    );

    const current = useMemo(() => {
        const nowMs = serverTime ?? Date.now();
        const list = accumulateTotals ? wishes : [wish];

        return list.reduce(
            (accumulator, currentWish) => accumulator + getCurrentTickets(currentWish, nowMs),
            0
        );
    }, [accumulateTotals, wish, wishes, serverTime]);

    const remaining = useMemo(() => {
        const nowMs = serverTime ?? Date.now();

        if (wish.type !== WishType.BUNDLE) {
            return Math.max(total - current, 0);
        }

        const parentRemaining = Math.max(wish.max_tickets - getCurrentTickets(wish, nowMs), 0);
        const childRemaining = bundledWishes.length > 0
            ? Math.min(...bundledWishes.map((bundledWish) => (
                Math.max(bundledWish.max_tickets - getCurrentTickets(bundledWish, nowMs), 0)
            )))
            : parentRemaining;

        return Math.max(Math.min(parentRemaining, childRemaining), 0);
    }, [bundledWishes, current, serverTime, total, wish]);

    return {current, total, remaining};
}

export default useWishTicketAvailability;
